import { useQuery } from '@tanstack/react-query';
import type { NotificationListResponse } from '@magimp/shared';
import { listNotifications } from '../../../api/notifications.js';

/**
 * @aiPurpose shared TanStack query for the bell + per-row badge surfaces.
 *   Splits the response into `unread` and `recent` (viewed within the
 *   30-day window) so callers don't re-derive the partition.
 * @aiInvariant the cache key is shared with #1071's realtime invalidator
 *   and with the per-row badge in #1072. Renaming or scoping the key
 *   would silently disconnect those consumers.
 */
export const NOTIFICATIONS_LIST_QK = ['notifications', 'list'] as const;

export interface NotificationsListSplit {
  data: NotificationListResponse;
  unread: NotificationListResponse;
  recent: NotificationListResponse;
  unreadAggregateCount: number;
}

export function useNotificationsList(options?: { enabled?: boolean }) {
  // WHY: the bell mounts inside the Topbar but the underlying request
  //   is authenticated. Allow callers to gate the query so the bell
  //   does not issue a protected GET while it is about to render null
  //   for unauthenticated callers (Codex review of #1070).
  return useQuery<NotificationListResponse>({
    queryKey: NOTIFICATIONS_LIST_QK,
    queryFn: listNotifications,
    enabled: options?.enabled ?? true,
  });
}

export function splitNotifications(rows: NotificationListResponse): NotificationsListSplit {
  // WHY: the bell panel always renders unread above recent, with unread
  //   sorted newest-first by `firstUnreadAt` and recent newest-first by
  //   `lastEventAt`. The backend doesn't promise an order so we sort here.
  const unread = rows
    .filter((r) => r.unreadCount > 0)
    .slice()
    .sort(
      (a, b) =>
        Date.parse(b.firstUnreadAt ?? b.lastEventAt) - Date.parse(a.firstUnreadAt ?? a.lastEventAt),
    );
  const recent = rows
    .filter((r) => r.unreadCount === 0)
    .slice()
    .sort((a, b) => Date.parse(b.lastEventAt) - Date.parse(a.lastEventAt));
  return {
    data: rows,
    unread,
    recent,
    unreadAggregateCount: unread.length,
  };
}
